import _ from 'lodash-es';
import config from '@/config';
import {
  createEvent,
  fetchEvents,
  fetchOneEvent,
  inviteToEvent,
  lockEvent,
  updateEventRule,
  deleteEventRule,
  createEventRule,
  cancelEvent,
  updateEventParticipant,
  deleteEventParticipant,
  createEventInvitation,
  consumeInvitation,
  fetchEventInvitations,
} from '@/modules/events/events.api';

const stateData = {
  list: undefined,
  current: undefined,
};

const getters = {
  getSecretSanta: ({ current }, _getters, rootState) => {
    if (!current || !current.participants) return undefined;
    const { userId } = rootState.auth.user;
    const secretSanta = _.find(current.participants, ({ user }) => user === userId);
    if (!secretSanta) return undefined;
    return _.find(current.participants, ({ user }) => user === secretSanta.secretSantaOfUser);
  },
  isOwnedAndUnlocked: ({ current }, _getters) => {
    if (!current || !current.ownerId) return false;
    if (current.locked) return false;

    return _getters.isOwned;
  },
  isOwned: ({ current }, _getters, rootState) => {
    console.log(current);
    if (!current || !current.ownerId) return false;
    const { id: userId } = rootState.auth.user;
    return current.ownerId === userId;
  },
  getValidInvitationUrl: ({ current }) => {
    if (!current || !current.latestInvitationCode) return undefined;
    return `${config.appUrl}/invite/${current.latestInvitationCode}`;
  },
};

const mutations = {
  setEvents(state, { events }) {
    state.list = events;
  },
  setCurrentEvent(state, { event }) {
    state.current = event;
  },
  deleteEvent(state, { eventId }) {
    if (!state.current) return;
    _.remove(state.list, { id: eventId });
  },
  updateEventParticipant(state, { participantId, label }) {
    if (!state.current) return;
    const { participants } = state.current;
    const indexOfParticipant = _.findIndex(participants, { id: participantId });
    state.current.participants[indexOfParticipant].label = label;
  },
  deleteEventParticipant(state, { participantId }) {
    if (!state.current) return;
    _.remove(state.current.participants, { id: participantId });
  },
  updateEventRule(state, { ruleId, condition, name }) {
    if (!state.current) return;
    const { rules } = state.current;
    const indexOfRule = _.findIndex(rules, { id: ruleId });
    state.current.rules[indexOfRule].condition = condition;
    state.current.rules[indexOfRule].name = name;
  },
  deleteEventRule(state, { ruleId }) {
    if (!state.current) return;
    _.remove(state.current.rules, { id: ruleId });
  },
  createEvent(state, { event }) {
    state.list.push(event);
  },
  createEventRule(state, { rule }) {
    if (!state.current) return;
    state.current.rules.push(rule);
  },
  createEventInvitation(state, { invitation }) {
    if (!state.current) return;
    state.current.latestInvitationCode = invitation.code;
  },
};

const actions = {
  async fetch({ commit, state }) {
    try {
      if (state.list) return;
      const { events } = await fetchEvents();
      commit('setEvents', { events });
    } catch (error) {
      console.error(error);
      throw new Error('Impossible de récupérer les évenements');
    }
  },
  async fetchOne({ commit, state }, { eventId }) {
    try {
      if (state.current && _.get(state.current, 'id') === eventId) return;
      const { event } = await fetchOneEvent({ eventId });
      const { invitations } = await fetchEventInvitations({ eventId });
      const latestInvitation = _.maxBy(invitations, 'createdAt');
      event.latestInvitationCode = latestInvitation?.code;
      commit('setCurrentEvent', { event });
    } catch (error) {
      console.error(error);
      throw new Error('Evenement introuvable');
    }
  },
  removeCurrent({ commit }) {
    commit('setCurrentEvent', { event: undefined });
  },
  cleanOnLogout({ commit }) {
    commit('setCurrentEvent', { event: undefined });
    commit('setEvents', { events: undefined });
  },
  async inviteToEvent({ commit, state }, { emails }) {
    try {
      const { id: eventId } = state.current;
      const { event } = await inviteToEvent({ eventId, emails });
      commit('setCurrentEvent', { event });
    } catch (error) {
      console.error(error);
      throw new Error("L'invitation a échoué. Veuillez réessayer ultérieurement");
    }
  },
  async lockEvent({ commit, state }) {
    try {
      const { id: eventId } = state.current;
      const { event } = await lockEvent({ eventId });
      commit('setCurrentEvent', { event });
    } catch (error) {
      console.error(error);
      throw new Error('Le tirage a échoué. Avez-vous assez de joueurs ou des règles de trop ?');
    }
  },
  async create({ commit, dispatch, state }, { name, startAt }) {
    try {
      const { event } = await createEvent({ name, startAt });
      commit('setCurrentEvent', { event });
      if (state.list) {
        commit('createEvent', { event });
        return event;
      }
      await dispatch('fetch');
      return event;
    } catch (error) {
      console.error(error);
      throw new Error("Impossible de créer l'évenement");
    }
  },
  async updateEventParticipant({ commit, state }, { participantId, label }) {
    try {
      const { id: eventId } = state.current;
      await updateEventParticipant({ eventId, participantId, label });
      commit('updateEventParticipant', { participantId, label });
    } catch (error) {
      console.error(error);
      throw new Error("Impossible de mettre à jour l'utilisateur");
    }
  },
  async deleteEventParticipant({ commit, state }, { participantId }) {
    try {
      const { id: eventId } = state.current;
      await deleteEventParticipant({ eventId, participantId });
      commit('deleteEventParticipant', { participantId });
    } catch (error) {
      console.error(error);
      throw new Error("Impossible de mettre à jour l'utilisateur");
    }
  },
  async updateEventRule({ state, commit }, { rule }) {
    try {
      const { id: eventId } = state.current;
      const { id: ruleId, condition, name } = rule;
      await updateEventRule({ eventId, ruleId, condition, name });
      commit('updateEventRule', { ruleId, condition, name });
    } catch (error) {
      console.error(error);
    }
  },
  async deleteEventRule({ state, commit }, { rule }) {
    try {
      const { id: eventId } = state.current;
      const { id: ruleId } = rule;
      await deleteEventRule({ eventId, ruleId });
      commit('deleteEventRule', { ruleId });
    } catch (error) {
      console.error(error);
    }
  },
  async createEventRule({ state, commit }) {
    try {
      const { id: eventId, participants } = state.current;
      const defaultUser = _.get(participants, '0.user.id');
      const condition = {
        '@userNotWith': {
          user: defaultUser,
          notWithUser: defaultUser,
        },
      };
      const { rule } = await createEventRule({ eventId, condition });
      commit('createEventRule', { rule });
    } catch (error) {
      console.error(error);
    }
  },
  async cancelEvent({ commit, state }) {
    try {
      const { id: eventId } = state.current;
      await cancelEvent({ eventId });
      commit('deleteEvent', { eventId });
    } catch (error) {
      console.error(error);
      throw new Error("Impossible d'annuler l'événement");
    }
  },
  async createEventInvitation({ commit, state }) {
    try {
      const { id: eventId } = state.current;
      const { invitation } = await createEventInvitation({ eventId });
      commit('createEventInvitation', { invitation });
    } catch (error) {
      console.error(error);
      throw new Error('Impossible de créer une invitation');
    }
  },
  async consumeInvitation({ commit }, { invitationCode }) {
    try {
      const { event } = await consumeInvitation({ invitationCode });
      commit('setCurrentEvent', { event });
      return event;
    } catch (error) {
      console.error(error);
      throw new Error("L'invitation a expiré");
    }
  },
};

export default {
  namespaced: true,
  state: { ...stateData },
  getters,
  mutations,
  actions,
};
